package com.ives.modules.adjustments;

import com.ives.components.notifier.Notifier;
import js.html.WheelEvent;
import haxe.Timer;

import js.html.TouchEvent;
import js.html.MouseEvent;

import com.ives.components.workspace.DicomWorkspace;
import com.ives.components.viewer.DicomViewer;
import com.ives.utils.math.Vector2D;
import com.ives.utils.math.MathStats;
import com.ives.utils.Log;
import com.ives.utils.DicomUtils;
import com.ives.utils.Remoting.Histogram;

class Adjustments extends Module{
	static public inline var ADJUSTMENTS_MODULE:String = "ADJUSTMENTS_MODULE";
	static public inline var ADJUSTMENTS_RESET:String = "ADJUSTMENTS_RESET";

	static public inline var ADJUSTMENTS_WINDOW:String = "ADJUSTMENTS_WINDOW";

	static private inline var ADJUSTMENTS_PAN:String = "ADJUSTMENTS_PAN";
	static private inline var ADJUSTMENTS_ZOOM:String = "ADJUSTMENTS_ZOOM";
	static private inline var ADJUSTMENTS_NEGATIVE:String = "ADJUSTMENTS_NEGATIVE";
	static private inline var ADJUSTMENTS_ROTATE:String = "ADJUSTMENTS_ROTATE";
	static private inline var ADJUSTMENTS_GESTURE:String = "ADJUSTMENTS_GESTURE";
	static private inline var ADJUSTMENTS_ROTATE_90:String = "ADJUSTMENTS_ROTATE_90";
	static private inline var ADJUSTMENTS_ROTATE_180:String = "ADJUSTMENTS_ROTATE_180";
	static private inline var ADJUSTMENTS_ROTATE_270:String = "ADJUSTMENTS_ROTATE_270";
	static private inline var ADJUSTMENTS_MIRROR:String = "ADJUSTMENTS_MIRROR";

	static private inline var ADJUSTMENTS_PAN_CLICK:String = "ADJUSTMENTS_PAN_CLICK";
	static private inline var ADJUSTMENTS_ZOOM_CLICK:String = "ADJUSTMENTS_ZOOM_CLICK";
	static private inline var ADJUSTMENTS_ROTATE_CLICK:String = "ADJUSTMENTS_ROTATE_CLICK";
	static private inline var ADJUSTMENTS_WINDOW_CLICK:String = "ADJUSTMENTS_WINDOW_CLICK";

	private var down:Bool = false;
	private var startX:Float;
	private var startY:Float;
	private var prevX:Float;
	private var prevY:Float;
	private var startAngle:Float;
	private var resizeBlock:Bool = false;

	private var timer:Timer;

	override public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		super.init(dicomViewer, dicomWorkspace);

		dicomWorkspace.on("mousedown", onMouseDown);
		dicomWorkspace.on("mousemove", onMouseMove);
		dicomWorkspace.on("mouseup", onMouseUp);
		dicomWorkspace.on("mouseout", onMouseOut);

		dicomWorkspace.on("touchstart", onTouchStart);
		dicomWorkspace.on("touchmove", onTouchMove);
		dicomWorkspace.on("touchend", onMouseUp);

		dicomWorkspace.on("wheel", onMouseWheel);
	}

	private function transformString():String{
		return 'translate(${dicomWorkspace.dicomImage.x}px, ${dicomWorkspace.dicomImage.y}px) scale(${dicomWorkspace.dicomImage.scale}, ${dicomWorkspace.dicomImage.scale}) rotate(${dicomWorkspace.dicomImage.rotation}deg)';
	}

	private function update(){
		dicomWorkspace.dicomImage.viewElement.style.transform = transformString();

		dispatchEvent(new ModuleEvent(DicomViewer.RESIZED));
	}

	override public function handleModuleEvent(event:ModuleEvent){
		if(event.type == ADJUSTMENTS_NEGATIVE)
			dicomWorkspace.dicomImage.negative = !dicomWorkspace.dicomImage.negative;

		if(event.type == ADJUSTMENTS_ROTATE_90)
			rotate(dicomWorkspace.dicomImage.rotation + 90);

		if(event.type == ADJUSTMENTS_ROTATE_180)
			rotate(dicomWorkspace.dicomImage.rotation + 180);

		if(event.type == ADJUSTMENTS_ROTATE_270)
			rotate(dicomWorkspace.dicomImage.rotation + 270);

		if(event.type == ADJUSTMENTS_ROTATE)
			rotate(event.points[0], event.points[1], event.points[2]);

		if(event.type == ADJUSTMENTS_PAN){
			resizeBlock = true;

			dicomWorkspace.dicomImage.x = event.points[0];
			dicomWorkspace.dicomImage.y = event.points[1];

			update();
		}

		if(event.type == ADJUSTMENTS_GESTURE){
			resizeBlock = true;

			dicomWorkspace.dicomImage.rotation = event.points[0];
			dicomWorkspace.dicomImage.scale = event.points[1];
			dicomWorkspace.dicomImage.x = event.points[2];
			dicomWorkspace.dicomImage.y = event.points[3];

			update();
		}

		if(event.type == ADJUSTMENTS_ZOOM){
			resizeBlock = true;

			var localImagePoint:Vector2D = new Vector2D(event.points[1], event.points[2]);
			var rotatedPoint:Vector2D = localImagePoint.clone();
			rotatedPoint.rotate(dicomWorkspace.dicomImage.rotation);

			var anchorPoint:Vector2D = imageToLocal([localImagePoint])[0];

			dicomWorkspace.dicomImage.scale = event.points[0];
			dicomWorkspace.dicomImage.x = anchorPoint.x - rotatedPoint.x * dicomWorkspace.dicomImage.scale;
			dicomWorkspace.dicomImage.y = anchorPoint.y - rotatedPoint.y * dicomWorkspace.dicomImage.scale;

			update();
		}

		if(event.type == ADJUSTMENTS_WINDOW){
			dicomWorkspace.dicomImage.setWindow(event.points[1], event.points[0]);

			updateHistogram();
		}

		if(event.type == ADJUSTMENTS_RESET){
			resizeBlock = false;

			reset();
		}

		if(event.type == DicomViewer.RESIZE && !resizeBlock)
			resetSize();

		if(event.type == ADJUSTMENTS_MIRROR){
			dicomWorkspace.dicomImage.mirrored = !dicomWorkspace.dicomImage.mirrored;

			dicomWorkspace.mirrorWarning.style.display = dicomWorkspace.dicomImage.mirrored ? "block" : "none";
		}

		if(event.type == ADJUSTMENTS_PAN_CLICK)
			Notifier.info('<span data-tr="notification.pan"></span> <kbd>Ctrl</kbd> + <lmb></lmb>');

		if(event.type == ADJUSTMENTS_ZOOM_CLICK)
			Notifier.info('<span data-tr="notification.zoom"></span> <kbd>Alt</kbd> + <lmb></lmb>');

		if(event.type == ADJUSTMENTS_ROTATE_CLICK)
			Notifier.info('<span data-tr="notification.rotate"></span> <kbd>Win/Cmd</kbd> + <lmb></lmb>');

		if(event.type == ADJUSTMENTS_WINDOW_CLICK)
			Notifier.info('<span data-tr="notification.window"></span> <kbd>Shift</kbd> + <lmb></lmb>');

		updateSliceInfo();
	}

	private function reset(){
		Log.debug('Reset');

		dicomWorkspace.dicomImage.negative = false;
		dicomWorkspace.dicomImage.mirrored = false;
		dicomWorkspace.mirrorWarning.style.display = "none";
		dicomWorkspace.dicomImage.setWindow(dicomWorkspace.dicomImageData.windowWidth ,dicomWorkspace.dicomImageData.windowCenter);

		resetSize();
	}

	private function resetSize(doDispatch:Bool = true){
		dicomWorkspace.dicomImage.rotation = 0;

		var resetZoom:Float = dicomWorkspace.viewElement.clientWidth / dicomWorkspace.dicomImage.width();

		if(dicomWorkspace.viewElement.clientWidth / dicomWorkspace.viewElement.clientHeight > dicomWorkspace.dicomImage.width() / dicomWorkspace.dicomImage.height())
			resetZoom = dicomWorkspace.viewElement.clientHeight / dicomWorkspace.dicomImage.height();

		var resetX:Float = (dicomWorkspace.viewElement.clientWidth - resetZoom * dicomWorkspace.dicomImage.width()) / 2;
		var resetY:Float = (dicomWorkspace.viewElement.clientHeight - resetZoom * dicomWorkspace.dicomImage.height()) / 2;

		dicomWorkspace.dicomImage.x = resetX;
		dicomWorkspace.dicomImage.y = resetY;
		dicomWorkspace.dicomImage.scale = resetZoom;

		update();
		updateSliceInfo();
	}

	private function rotate(angle:Float, ?x:Float = null, ?y:Float = null){
		resizeBlock = true;

		if(x == null){
			x = dicomWorkspace.dicomImage.width() / 2;
			y = dicomWorkspace.dicomImage.height() / 2;
		}

		var localImagePoint:Vector2D = new Vector2D(x, y);
		var rotatedPoint:Vector2D = localImagePoint.clone();
		rotatedPoint.rotate(angle);
		var anchorPoint:Vector2D = imageToLocal([localImagePoint])[0];

		dicomWorkspace.dicomImage.rotation = angle;
		dicomWorkspace.dicomImage.x = anchorPoint.x - rotatedPoint.x * dicomWorkspace.dicomImage.scale;
		dicomWorkspace.dicomImage.y = anchorPoint.y - rotatedPoint.y * dicomWorkspace.dicomImage.scale;

		update();
	}

	private function onMouseDown(event:MouseEvent){
//		if(!dicomViewer.adjustmentsButton.checked && !hasModifier(event))
//			return;

		startX = prevX = event.pageX - dicomWorkspace.viewElement.offsetLeft;
		startY = prevY = event.pageY - dicomWorkspace.viewElement.offsetTop;

		down = true;
	}

	private var touches:Array<Vector2D>;

	private function onTouchStart(event:TouchEvent){
		event.preventDefault();

		if(event.touches.length == 1){
//			if(!dicomViewer.adjustmentsButton.checked)
//				return;

			startX = prevX = event.touches.item(0).pageX - dicomWorkspace.viewElement.offsetLeft;
			startY = prevY = event.touches.item(0).pageY - dicomWorkspace.viewElement.offsetTop;
		}
		else if(event.touches.length > 1){
			touches = localToImage([new Vector2D(event.touches.item(0).pageX - dicomWorkspace.viewElement.offsetLeft, event.touches.item(0).pageY - dicomWorkspace.viewElement.offsetTop), new Vector2D(event.touches.item(1).pageX - dicomWorkspace.viewElement.offsetLeft, event.touches.item(1).pageY - dicomWorkspace.viewElement.offsetTop)]);
		}

		down = true;
	}

	private function onTouchMove(event:TouchEvent){
		var currentX:Float = event.touches.item(0).pageX - dicomWorkspace.viewElement.offsetLeft;
		var currentY:Float = event.touches.item(0).pageY - dicomWorkspace.viewElement.offsetTop;

		if(event.touches.length == 1){
//			if(!dicomViewer.adjustmentsButton.checked)
//				return;

			handleMove(currentX, currentY);
		}
		else if(event.touches.length == 2){
			var d:Vector2D = touches[0].clone();
			d.decrement(touches[1]);
			var df:Float = d.length();

			var currentTouches:Array<Vector2D> = [new Vector2D(event.touches.item(0).pageX - dicomWorkspace.viewElement.offsetLeft, event.touches.item(0).pageY - dicomWorkspace.viewElement.offsetTop), new Vector2D(event.touches.item(1).pageX - dicomWorkspace.viewElement.offsetLeft, event.touches.item(1).pageY - dicomWorkspace.viewElement.offsetTop)];

			var c:Vector2D = currentTouches[0].clone();
			c.decrement(currentTouches[1]);
			var cf:Float = c.length();

			var zoom:Float = cf / df; // new scale

			var origin:Vector2D = touches[0].clone();
			origin.scale(-1);

			var oldAngle:Float = Math.atan2(touches[1].y - touches[0].y, touches[1].x - touches[0].x);
			var newAngle:Float = Math.atan2(currentTouches[1].y - currentTouches[0].y, currentTouches[1].x - currentTouches[0].x);

			var dAngle:Float = 180 * (newAngle - oldAngle) / Math.PI;

			origin.rotate(dAngle);
			origin.scale(zoom);
			origin.increment(currentTouches[0]);

			dispatchEvent(new ModuleEvent(ADJUSTMENTS_GESTURE, [dAngle, zoom, origin.x, origin.y], workspaceIdForEvent(), false, true));
		}
		else if(event.touches.length == 3){
			var dx:Float = currentX - prevX;
			var dy:Float = currentY - prevY;

			dispatchEvent(new ModuleEvent(ADJUSTMENTS_WINDOW, [dicomWorkspace.dicomImage.windowCenter + dx, dicomWorkspace.dicomImage.windowWidth - dy], workspaceIdForEvent(), false, true));

			prevX = currentX;
			prevY = currentY;
		}
	}

	private function onMouseWheel(event:WheelEvent){
		if(event.ctrlKey){
			event.preventDefault();

			var currentX:Float = event.pageX - dicomWorkspace.viewElement.offsetLeft;
			var currentY:Float = event.pageY - dicomWorkspace.viewElement.offsetTop;

			var zoom:Float = dicomWorkspace.dicomImage.scale - event.deltaY / 250;

			if(zoom > 10 || zoom < 0.05)
				return;

			var imageFloats:Array<Float> = localToImageFloats([currentX, currentY]);

			dispatchEvent(new ModuleEvent(ADJUSTMENTS_ZOOM, [zoom, imageFloats[0], imageFloats[1]], workspaceIdForEvent(), false, true));
		}
	}

	private function onMouseMove(event:MouseEvent){
//		if(!dicomViewer.adjustmentsButton.checked && !hasModifier(event))
//			return;

		if(!down)
			return;

		var currentX:Float = event.pageX - dicomWorkspace.viewElement.offsetLeft;
		var currentY:Float = event.pageY - dicomWorkspace.viewElement.offsetTop;

		handleMove(currentX, currentY, event);
	}

	private function handleMove(currentX:Float, currentY:Float, ?event:MouseEvent){
		var dx:Float = currentX - prevX;
		var dy:Float = currentY - prevY;
		var delta:Float = Math.sqrt(dx * dx + dy * dy);

		if((dicomViewer.panButton.checked && !hasModifier(event)) || (event != null && event.ctrlKey))
			dispatchEvent(new ModuleEvent(ADJUSTMENTS_PAN, [dicomWorkspace.dicomImage.x + dx, dicomWorkspace.dicomImage.y + dy], workspaceIdForEvent(), false, true));

		if((dicomViewer.zoomButton.checked && !hasModifier(event)) || (event != null && event.altKey)){
			var zoom:Float = dicomWorkspace.dicomImage.scale - dy / 100;

			if(zoom > 10 || zoom < 0.05)
				return;

			var imageFloats:Array<Float> = localToImageFloats([startX, startY]);

			dispatchEvent(new ModuleEvent(ADJUSTMENTS_ZOOM, [zoom, imageFloats[0], imageFloats[1]], workspaceIdForEvent(), false, true));
		}

		if((dicomViewer.rotateButton.checked && !hasModifier(event)) || (event != null && event.metaKey)){
			var angle:Float = 180 * Math.atan2(currentY - startY, currentX - startX) / Math.PI;

			var handle:Vector2D = new Vector2D(currentX, currentY);
			var start:Vector2D = new Vector2D(startX, startY);
			var toHandle:Vector2D = handle.subtract(start);
			var length:Float = toHandle.length();
			var level:Vector2D = new Vector2D(startX + 32, startY);

			level.decrement(start);
			var levelLength:Float = level.length();
			level.increment(start);

			toHandle.normalize();
			toHandle.scale(levelLength);
			toHandle.increment(start);

			//addDrawing(new Drawing(Drawing.SHAPE, [toHandle.x, toHandle.y, startImageX, startImageY, level.x, level.y]));

			var offset:Vector2D = getOffset();

			var imageFloats:Array<Float> = localToImageFloats([startX - offset.x, startY - offset.y]);

			if(length < 32)
				startAngle = dicomWorkspace.dicomImage.rotation - angle;
			else
				dispatchEvent(new ModuleEvent(ADJUSTMENTS_ROTATE, [startAngle + angle, imageFloats[0], imageFloats[1]], workspaceIdForEvent(), false, true));
		}

		if((dicomViewer.windowButton.checked && !hasModifier(event)) || (event != null && event.shiftKey)){
			var scale:Float = dicomWorkspace.dicomImageData.windowWidth / 512;

			dispatchEvent(new ModuleEvent(ADJUSTMENTS_WINDOW, [dicomWorkspace.dicomImage.windowCenter + dx * scale, dicomWorkspace.dicomImage.windowWidth - dy * scale], workspaceIdForEvent(), false, true));
		}

		prevX = currentX;
		prevY = currentY;
	}

	private function onMouseUp(event:MouseEvent){
		down = false;
	}

	private function onMouseOut(event:MouseEvent){
		down = false;
	}

	private function hideHistogram(){
		histogramContainer.classList.remove("show");
	}

	private function updateHistogram(){
		if(dicomWorkspace.dicomImage.histogram != null){
			histogramContainer.style.display = "block";

			histogramContainer.classList.add("show");

			var histogramWidth:Float = histogramContainer.clientWidth;
			var histogramHeight:Float = histogramContainer.clientHeight;

			var path:String = 'M 0 ${histogramHeight}';

			var barWidth:Float = histogramWidth / (dicomWorkspace.dicomImage.histogram.length - 1);

			for(n in 1...dicomWorkspace.dicomImage.histogram.length){
				var height:Float = histogramHeight * dicomWorkspace.dicomImage.histogram[n] / dicomWorkspace.dicomImage.histogramMaxCount;

				path += ' L ${(n - 1) * barWidth} ${histogramHeight - height}';
			}

			path += ' L ${histogramWidth} ${histogramHeight} z';

			histogramPath.setAttribute("d", path);

			var x:Float = histogramWidth * (dicomWorkspace.dicomImage.windowCenter - dicomWorkspace.dicomImage.histogramMin) / dicomWorkspace.dicomImage.histogramWidth;
			windowCenter.style.left = '${x}px';

			x = histogramWidth * (dicomWorkspace.dicomImage.windowCenter + dicomWorkspace.dicomImage.windowWidth/2 - dicomWorkspace.dicomImage.histogramMin) / dicomWorkspace.dicomImage.histogramWidth;
			windowLeft.style.left = '${x}px';

			x = histogramWidth * (dicomWorkspace.dicomImage.windowCenter - dicomWorkspace.dicomImage.windowWidth/2 - dicomWorkspace.dicomImage.histogramMin) / dicomWorkspace.dicomImage.histogramWidth;
			windowRight.style.left = '${x}px';

			if(timer != null)
				timer.stop();

			timer = Timer.delay(hideHistogram, 500);
		}

		if(dicomWorkspace.dicomImage.remoteHistogram != null){
			var histogram:Histogram = dicomWorkspace.dicomImage.remoteHistogram;

			histogramContainer.style.display = "block";
			histogramContainer.classList.add("show");

			var histogramWidth:Float = histogramContainer.clientWidth;
			var histogramHeight:Float = histogramContainer.clientHeight;

			var path:String = 'M 0 ${histogramHeight}';

			var barWidth:Float = histogramWidth / (histogram.values.length - 1);

			var maxLog:Float = Math.log(histogram.maxCount - histogram.minCount) / Math.log(10);

			for(n in 1...histogram.values.length){
				var value:Float = histogram.values[n];

				if(value > histogram.minCount)
					value = Math.log(value - histogram.minCount) / Math.log(10);
				else
					value = 0;

				var height:Float = histogramHeight * value / maxLog;

				path += ' L ${(n - 1) * barWidth} ${histogramHeight - height}';
			}

			path += ' L ${histogramWidth} ${histogramHeight} z';

			histogramPath.setAttribute("d", path);

			var histogramWidthValue:Float = histogram.maxValue - histogram.minValue;

			var x:Float = histogramWidth * (dicomWorkspace.dicomImage.windowCenter - histogram.minValue) / histogramWidthValue;
			windowCenter.style.left = '${x}px';

			x = histogramWidth * (dicomWorkspace.dicomImage.windowCenter + dicomWorkspace.dicomImage.windowWidth/2 - histogram.minValue) / histogramWidthValue;
			windowLeft.style.left = '${x}px';

			x = histogramWidth * (dicomWorkspace.dicomImage.windowCenter - dicomWorkspace.dicomImage.windowWidth/2 - histogram.minValue) / histogramWidthValue;
			windowRight.style.left = '${x}px';

			if(timer != null)
				timer.stop();

			timer = Timer.delay(hideHistogram, 500);
		}
	}

	private function updateSliceInfo(){
		dicomWorkspace.windowValue.innerHTML = '${MathStats.roundTo(dicomWorkspace.dicomImage.windowCenter, 1)}/${MathStats.roundTo(dicomWorkspace.dicomImage.windowWidth, 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';
		dicomWorkspace.zoomValue.innerHTML = '${MathStats.roundTo(dicomWorkspace.dicomImage.scale * 100, 1)}%';

		var rot:Float = (dicomWorkspace.dicomImage.rotation % 360 + 360) % 360;

		if(rot > 180)
			rot -= 360;

		dicomWorkspace.rotationValue.innerHTML = '${MathStats.roundTo(rot, 1)}°';
	}
}
